import { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import useAuth from '../../../auth/useAuth';
import Layout from '../../UI/introGameLayout/layout';
import OnboardingProcess from '../../UI/instructor/onboarding/process';
import Alert from '../../UI/alert';

import {
  coursesQuery,
  createScenario,
  coursesSetupQuery,
  scenarioBulkAddCourse,
  createGame,
  createScenarioLevels,
  createItemQuestionsBulk,
  createItemOptionsBulk,
  scenarioLevelsQuery,
  updateCourse,
  createGameSession
} from './api';

import { generalInfoQuery } from '../settings/assignments/api';

import {
  gameTypes,
  scenarioLevelsDescription,
  gameCreateItemsQuestionsColumns,
  gameCreateItemsOptionsClassification,
} from './data';

export default function InstructorOnboarding() {
  const navigate = useNavigate();
  const { saveCurrentGameId, saveCurrentScenarioId, getUser } = useAuth();
  const user = getUser();

  const [selectedBusinessType, setSelectedBusinessType] = useState(null);
  const [isCreatingGame, setIsCreatingGame] = useState(false);
  const [demoGameError, setDemoGameError] = useState(null);
  const [gameCreated, setGameCreated] = useState(false);
  const [isLoadingStep, setIsLoadingStep] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const existingSession = generalInfo?.sessions?.find(
    session => session.user === user?.id
  ) || null;

  const { data: courses } = useQuery(coursesQuery());

  const { refetch: refetchCoursesSetup } = useQuery({
    ...coursesSetupQuery(),
    enabled: false
  });

  const queryClient = useQueryClient();

  const refetchScenarioLevels = async (scenarioId) => {
    const data = await queryClient.fetchQuery({
      ...scenarioLevelsQuery({ scenarioId })
    });
    return data;
  };

  const createScenarioMutation = useMutation({
    mutationFn: (data) => createScenario(data)
  });

  const createGameMutation = useMutation({
    mutationFn: (data) => createGame(data)
  });

  const createScenarioLevelsMutation = useMutation({
    mutationFn: (data) => createScenarioLevels(data)
  });

  const createItemQuestionsBulkMutation = useMutation({
    mutationFn: (data) => createItemQuestionsBulk(data)
  });

  const createItemOptionsBulkMutation = useMutation({
    mutationFn: (data) => createItemOptionsBulk(data)
  });

  const updateCourseMutation = useMutation({
    mutationFn: (data) => updateCourse(data)
  });

  const handleSelectBusinessType = ({ type }) => {
    setSelectedBusinessType(type);

    // Immediately persist the instructor's Step 1 choice on the
    // course record. The backend businesstype enum is case-sensitive
    // (CORPORATION = "Corporation", NONCORPORATE = "Noncorporate") —
    // sending lowercase "noncorporate" 4xx's the PATCH.
    const currentCourseId = courses?.[0]?.id;
    if (!currentCourseId) return;
    const courseBusinessType = type === 'corporate' ? 'Corporation' : 'Noncorporate';
    updateCourseMutation.mutateAsync({
      id: currentCourseId,
      businessType: courseBusinessType,
    }).catch((err) => {
      console.error('Failed to update course business type on Step 1 select:', err);
    });
  }

  const createLevel = async (scenarioId, gameType, config) => {
    const { name, levelNumber, description, questions, options, useDirectMappingQuestions, useDirectMappingOptions, itemLookup, design } = config;

    try {
      const resultCreateScenarioLevel = await createScenarioLevelsMutation.mutateAsync({
        name,
        levelNumber,
        scenarios: [scenarioId],
        description,
        design
      });

      if (!resultCreateScenarioLevel?.id) {
        console.error(`Failed to create scenario level ${levelNumber}`);
        return;
      }

      const scenarioLevelId = resultCreateScenarioLevel.id;

      const bulkQuestions = questions.map((question, index) => {
        if (useDirectMappingQuestions) {
          const matchedGamesItem = itemLookup?.[question];
          if (!matchedGamesItem) return null;
          return { accountItemId: matchedGamesItem.id, order: matchedGamesItem.order || index + 1 };
        } else {
          return {
            accountItemId: question.account_item?.id || question.transaction_id || question.id,
            order: question.order || index + 1
          };
        }
      }).filter(Boolean);

      if (bulkQuestions.length) {
        await createItemQuestionsBulkMutation.mutateAsync({
          gameType,
          questions: bulkQuestions,
          scenarioLevelId
        }).catch(error => {
          console.error(`Failed to create questions for level ${levelNumber}:`, error);
        });
      }

      if (!options?.length) return;

      const bulkOptions = options.map((option, index) => {
        if (useDirectMappingOptions) {
          const matchedGamesItem = itemLookup?.[option];
          if (!matchedGamesItem) return null;
          return { accountItemId: matchedGamesItem.id, order: matchedGamesItem.order || index + 1 };
        } else {
          return {
            accountItemId: option.account_item?.id || option.id,
            order: option.order
          };
        }
      }).filter(Boolean);

      if (bulkOptions.length) {
        await createItemOptionsBulkMutation.mutateAsync({
          gameType,
          options: bulkOptions,
          scenarioLevelId
        }).catch(error => {
          console.error(`Failed to create options for level ${levelNumber}:`, error);
        });
      }

    } catch (error) {
      console.error(`Error creating level ${levelNumber}:`, error);
    }
  }

  // Synchronous guard — the isCreatingGame state flip is async, so two parallel invocations can both pass that check before React re-renders. The ref closes the window.
  const isCreatingDemoGameRef = useRef(false);

  const createDemoGame = useCallback(async () => {
    if (isCreatingDemoGameRef.current || isCreatingGame || gameCreated || !courses?.length || !selectedBusinessType) return;
    isCreatingDemoGameRef.current = true;
    setIsCreatingGame(true);

    try {
      const courseIds = courses.map(course => course.id);
      const currentCourseId = courseIds[0];

      // Course businessType is already persisted on the Step 1
      // selection, so no second updateCourse call is needed here.

      // Fetch course setup and only use "onboarding-demo" scenario
      const { data: coursesSetupData } = await refetchCoursesSetup();

      const onboardingDemoSetup = coursesSetupData?.find(setup => setup.name === 'onboarding-demo');

      if (!onboardingDemoSetup) {
        console.error('onboarding-demo scenario not found in course setup');
        setIsCreatingGame(false);
        return;
      }

      // Add course to the onboarding-demo scenario only
      await scenarioBulkAddCourse({
        scenarioIds: [onboardingDemoSetup.id],
        courseId: currentCourseId
      });

      // Create a new scenario for the Classification demo game
      const resultCreateScenario = await createScenarioMutation.mutateAsync({
        name: 'Demo Assignment',
        courses: courseIds
      });

      if (!resultCreateScenario?.id) {
        console.error('Failed to create scenario');
        setIsCreatingGame(false);
        return;
      }

      // Create the Classification game
      const classificationGameType = gameTypes.find(type => type.id === 'classification');
      const startTime = moment().toISOString();
      const endTime = moment().add(30, 'days').toISOString();

      const resultCreateGame = await createGameMutation.mutateAsync({
        name: 'Demo Assignment',
        description: classificationGameType.description,
        startTime,
        endTime,
        gameType: 'classification',
        scenarioId: resultCreateScenario.id,
        courseId: currentCourseId
      }).catch((error) => {
        console.error('Error creating game:', error);
        return null;
      });

      if (!resultCreateGame) {
        console.error('Failed to create Classification demo game');
        setIsCreatingGame(false);
        return;
      }

      // Fetch scenario levels from the onboarding-demo template
      const demoScenarioLevels = await refetchScenarioLevels(onboardingDemoSetup.id);

      // Build item lookup from the demo scenario levels
      const itemLookup = {};
      demoScenarioLevels.forEach(level => {
        (level.questions || []).forEach(q => {
          if (q.account_item) itemLookup[q.account_item.name] = q.account_item;
        });
        (level.options || []).forEach(o => {
          if (o.account_item) itemLookup[o.account_item.name] = o.account_item;
        });
      });

      // Level 1: class-elements
      const classElementsLevel = demoScenarioLevels.find(level => level.name === 'class-elements');

      const level1Config = {
        name: 'class-elements',
        levelNumber: 1,
        description: classElementsLevel?.description || scenarioLevelsDescription['classification'][0],
        questions: gameCreateItemsQuestionsColumns,
        options: gameCreateItemsOptionsClassification,
        useDirectMappingQuestions: true,
        useDirectMappingOptions: true,
        itemLookup,
        design: ['1']
      };

      await createLevel(resultCreateScenario.id, 'classification', level1Config);

      // Create a game session so the game component can function
      await createGameSession({ game: resultCreateGame.id, user: user?.id });

      // Save game ID so the Classification router can find it
      saveCurrentGameId(resultCreateGame.id, 'Account Classification');
      saveCurrentScenarioId(resultCreateScenario.id);

      // Invalidate general-info cache so the tutorial router picks up the new game
      queryClient.invalidateQueries({ queryKey: ['generalInfo'] });

      setGameCreated(true);
      setIsCreatingGame(false);

    } catch (error) {
      console.error('Error creating demo game:', error);
      setIsCreatingGame(false);
      setDemoGameError({
        type: 'error',
        title: 'Something Went Wrong',
        text: 'We couldn\'t set up your demo assignment. Please try again — if the issue continues, refresh the page.'
      });
    } finally {
      isCreatingDemoGameRef.current = false;
    }
  }, [courses, selectedBusinessType, isCreatingGame, gameCreated]);

  return (
    <Layout
      isHeaderVisible={false}
      isContentVertical={true}
      backgroundColor={isLoadingStep ? '#fff' : 'linear-gradient(135deg, #EC5E59 2.88%, rgba(146, 219, 225, 0.8) 99.03%)'}
      className="instructor-onboarding-layout"
    >
      <OnboardingProcess
        onSelectBusinessType={handleSelectBusinessType}
        selectedBusinessType={selectedBusinessType}
        onCreateDemoGame={createDemoGame}
        gameCreated={gameCreated}
        existingSession={existingSession}
        onStepChange={(step) => setIsLoadingStep(step === 2)}
      />
      {demoGameError && (
        <Alert
          type={demoGameError.type}
          title={demoGameError.title}
          text={demoGameError.text}
          confirmButtonText="Ok"
          onConfirmButtonClick={() => setDemoGameError(null)}
        />
      )}
    </Layout>
  );
}
